import { Link } from 'react-router-dom';
import { GraduationCap, Clock, ArrowRight, Radio, Calendar, Award, Utensils } from 'lucide-react';
import { isLiveLike, liveBadgeLabel } from '../lib/course-types';
import { formatPromoEnd, promoModeLabel } from '../lib/promo';

export default function FeaturedCourseCard({ course }) {
  return (
    <Link
      to={`/courses/${course.slug}`}
      className="block bg-white rounded-2xl overflow-hidden border border-orange-100/60 hover:shadow-xl hover:shadow-brand/10 transition-all group"
    >
      <div className="grid md:grid-cols-2">
        {/* Left — image */}
        <div className="relative">
          {(course.featured_image_url || course.hero_image_url) ? (
            <img
              src={course.featured_image_url || course.hero_image_url}
              alt=""
              className="w-full h-full object-cover min-h-[240px] md:min-h-[320px]"
            />
          ) : (
            <div className="w-full h-full min-h-[240px] md:min-h-[320px] bg-gradient-to-br from-brand/15 to-brand/5 flex items-center justify-center">
              <GraduationCap className="text-brand/40" size={64} />
            </div>
          )}
        </div>

        {/* Right — details */}
        <div className="p-8 md:p-10 flex flex-col justify-center">
          <h3 className="text-2xl md:text-3xl font-extrabold text-slate-900 group-hover:text-brand transition-colors leading-tight">
            {course.title}
          </h3>

          {course.short_description && (
            <p className="mt-4 text-slate-600 leading-relaxed">
              {course.short_description}
            </p>
          )}

          <div className="mt-5 flex flex-wrap gap-4 text-sm text-slate-500">
            {isLiveLike(course.course_type) && (
              <span className="inline-flex items-center gap-1.5 text-red-600 font-semibold">
                <Radio size={14} className="animate-pulse" /> {liveBadgeLabel(course.course_type)}
              </span>
            )}
            {isLiveLike(course.course_type) && course.live_date_utc && (
              <span className="inline-flex items-center gap-1.5 text-brand font-semibold">
                <Calendar size={14} /> {new Date(course.live_date_utc).toLocaleDateString('en-US', { timeZone: 'America/New_York', weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
              </span>
            )}
            {isLiveLike(course.course_type) && course.live_date_utc && (
              <span className="inline-flex items-center gap-1.5 font-semibold">
                <Clock size={14} /> {new Date(course.live_date_utc).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })} ET
              </span>
            )}
            {course.duration_minutes > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={14} /> {course.duration_minutes} min
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
              <Award size={14} /> Certificate on completion
            </span>
            {course.course_type === 'hybrid' && course.lunch_included && (
              <span className="inline-flex items-center gap-1.5">
                <Utensils size={14} /> Lunch included (in person)
              </span>
            )}
          </div>

          {course.price_cents != null && (
            <div className="mt-4">
              <span className="font-bold text-brand text-2xl">
                {course.price_cents === 0 ? 'Free' : `$${(course.price_cents / 100).toFixed(2)}`}
              </span>
            </div>
          )}

          {course.promo && (
            <div className="mt-3 flex flex-wrap items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span className="text-red-600 text-base font-extrabold">${(course.promo.special_price_cents / 100).toFixed(2)}</span>
              <span className="text-red-500 text-sm line-through">${(course.promo.original_price_cents / 100).toFixed(2)}</span>
              <span className="text-red-300">|</span>
              <span className="text-red-700 text-sm font-semibold">Code: <span className="font-mono tracking-wide">{course.promo.code}</span></span>
              {promoModeLabel(course.promo) && (
                <>
                  <span className="text-red-300">|</span>
                  <span className="text-red-700 text-xs font-semibold">{promoModeLabel(course.promo)}</span>
                </>
              )}
              {course.promo.expires_at && (
                <>
                  <span className="text-red-300">|</span>
                  <span className="text-red-600 text-xs font-semibold">{formatPromoEnd(course.promo.expires_at)}</span>
                </>
              )}
            </div>
          )}

          <div className="mt-8">
            <span className="inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-full text-sm group-hover:bg-brand/90 transition-colors">
              View Course
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
